import { Component, inject, OnInit } from '@angular/core';
import { AppService } from '../../../shared/services/app.service';
import {
  AudienceType,
  ChannelType,
  Engagements,
  ExecutionStatus,
  ExportAudienceMutation,
  ExportAudienceMutationVariables,
  GetModelsQuery,
  GetModelsQueryVariables,
  GetQueryRecordsQuery,
  GetQueryRecordsQueryVariables,
  Model,
  ModelType,
  ModelStatus,
  QueryRecord,
  QueryType,
} from '../../../../API';
import { ApiService } from '../../../shared/services/api.service';
import { GetModels, GetQueryRecords } from '../../../../graphql/queries';
import { NzMessageService } from 'ng-zorro-antd/message';
import { ExportAudience } from '../../../../graphql/mutations';
import { UserManagementService } from '../../../shared/services/user-management.service';
import { FormArray, FormBuilder, FormControl, FormGroup, Validators, UntypedFormGroup } from '@angular/forms';
import { NZ_MODAL_DATA, NzModalService } from 'ng-zorro-antd/modal';
import { differenceInCalendarDays } from 'date-fns';
import { GoogleMappings, Mappings, TypeDropdown } from '../../models/query-interface';
import { Router } from '@angular/router';
import { S3_FOLDER_PATH_PATTERN } from '../../../shared/utils/constants';
import { distinctUntilChanged } from 'rxjs/operators';

@Component({
  selector: 'app-export-audience-modal',
  templateUrl: './export-audience-modal.component.html',
  styleUrl: './export-audience-modal.component.scss',
})
export class ExportAudienceModalComponent implements OnInit {
  engagement!: string;
  isLoading = false;
  selectAudienceOptions?: QueryRecord[];
  selectModelOptions?: Model[];

  exportAudienceForm: UntypedFormGroup;
  expiryAtMinDate = new Date();
  queryRecord: QueryRecord | undefined;
  selectedModel: Model | undefined;

  mappingOptions: any = Mappings;
  googleMappingOptions: any = [
    {
      title: 'Email',
      value: GoogleMappings.EMAIL,
    },
    {
      title: 'Phone',
      value: GoogleMappings.PHONE,
    },
  ];
  readonly activationChannel: any = inject(NZ_MODAL_DATA);
  loadingModels: boolean = false;
  readonly fileTypes: TypeDropdown[] = [
    {
      name: 'CSV',
      value: 'CSV',
    },
    {
      name: 'Parquet',
      value: 'PARQUET',
    },
  ];
  loadingQueries: boolean = true;
  readonly audienceTypes = AudienceType;

  constructor(
    private appService: AppService,
    private apiService: ApiService,
    private nzMessageService: NzMessageService,
    private userManagementService: UserManagementService,
    private nzModalService: NzModalService,
    private fb: FormBuilder,
    private router: Router,
  ) {
    this.exportAudienceForm = this.fb.nonNullable.group({
      audienceFrom: new FormControl(AudienceType.QUERY, [Validators.required]),
      queryId: new FormControl('', [Validators.required]),
    });
  }

  ngOnInit() {
    this.appService.activeEngagement$.subscribe((engagement: Engagements) => {
      this.engagement = engagement.engagement;
      this.getSelectAudienceOptions();
      this.selectRightFormControls();
    });

    // Subscribe to audienceFrom value changes
    this.audienceFromControl.valueChanges.pipe(distinctUntilChanged()).subscribe((value) => {
      this.onAudienceFromChange(value);
    });
  }
  selectRightFormControls() {
    if (this.activationChannel.channelType === ChannelType.META) {
      this.exportAudienceForm.addControl('expireAt', new FormControl('', [Validators.required])),
        this.exportAudienceForm.addControl('attributesMapping', this.fb.nonNullable.array([]));
    } else if (this.activationChannel.channelType === ChannelType.GOOGLE) {
      this.exportAudienceForm.addControl('expireAt', new FormControl('', [Validators.required])),
        this.exportAudienceForm.addControl('attributesMapping', this.fb.nonNullable.array([]));
    } else if (this.activationChannel.channelType === ChannelType.S3) {
      this.exportAudienceForm.addControl(
        'folderPath',
        new FormControl('', [Validators.pattern(S3_FOLDER_PATH_PATTERN)]),
      );
      this.exportAudienceForm.addControl('fileType', this.fb.control('CSV', Validators.required));
    }
  }

  loadAttributes(attributes: string[]): void {
    this.attributesMappingFormArray.clear();

    attributes.forEach((attribute) => {
      this.attributesMappingFormArray.push(
        this.fb.group({
          attribute: new FormControl(attribute),
          mapping: new FormControl(''),
        }),
      );
    });
  }

  getSelectAudienceOptions(): void {
    this.apiService
      .query<GetQueryRecordsQuery, GetQueryRecordsQueryVariables>(GetQueryRecords, {
        engagementId: this.engagement,
      })
      .then((response) => {
        const result = response.data?.GetQueryRecords;
        if (result) {
          this.selectAudienceOptions = result?.filter(
            (row) => row?.type === QueryType.LIST && row.executionStatus === ExecutionStatus.SUCCESS,
          ) as QueryRecord[];
        }
      })
      .catch((error) => {
        this.nzMessageService.error(`${error} Failed to get options of Select Audience`);
      })
      .finally(() => {
        this.loadingQueries = false;
      });
  }

  getSelectModelOptions(): void {
    this.loadingModels = true;
    this.apiService
      .query<GetModelsQuery, GetModelsQueryVariables>(GetModels, {
        engagementId: this.engagement,
      })
      .then((response) => {
        const result = response.data?.GetModels;
        if (result) {
          this.selectModelOptions = result?.filter(
            (model) => model?.modelType === ModelType.LOOKALIKE && model.status === ModelStatus.SUCCESS,
          ) as Model[];
        }
      })
      .catch((error) => {
        this.nzMessageService.error(`${error} Failed to get options of Select Models`);
      })
      .finally(() => {
        this.loadingModels = false;
      });
  }

  getOptionDisplayName(option: QueryRecord): string {
    return `${option.name} ( ${option.queryId} )`;
  }

  getModelDisplayName(model: Model): string {
    return `${model.name} ( ${model.modelId} )`;
  }

  exportAudience(): void {
    let attributesMapping;
    if (this.activationChannel.channelType === ChannelType.META) {
      if (!this.isMappingEmpty) {
        this.nzMessageService.error('Please select mapping for all attributes');
        return;
      }
      if (this.isDuplicateMappings) {
        this.nzMessageService.error('Please select unique mapping for all attributes');
        return;
      }
      attributesMapping = JSON.stringify(this.attributesMappingFormArray.value);
    } else if (this.activationChannel.channelType === ChannelType.GOOGLE) {
      if (!this.isMappingEmpty) {
        this.nzMessageService.error('Please select mapping for all attributes');
        return;
      }
      if (this.isDuplicateMappings) {
        this.nzMessageService.error('Please select unique mapping for all attributes');
        return;
      }
      if (!this.hasValidGoogleMappings) {
        const totalColumns = this.attributesMappingFormArray.controls.length;
        if (totalColumns === 1) {
          this.nzMessageService.error('Please select either Email or Phone mapping for the available column');
        } else {
          this.nzMessageService.error(
            'Both Email and Phone mappings are required for Google channel when multiple columns are available',
          );
        }
        return;
      }
      attributesMapping = JSON.stringify(this.attributesMappingFormArray.value);
    }
    this.isLoading = true;
    const { queryId, expireAt, folderPath, fileType, audienceFrom } = this.exportAudienceForm.value;

    let queryName: string;
    if (audienceFrom === AudienceType.LOOKALIKE_MODEL) {
      // eslint-disable-next-line @typescript-eslint/no-non-null-asserted-optional-chain
      queryName = this.selectModelOptions?.find((item) => item.modelId === queryId)?.name!;
    } else {
      // eslint-disable-next-line @typescript-eslint/no-non-null-asserted-optional-chain
      queryName = this.selectAudienceOptions?.find((item) => item.queryId === queryId)?.name!;
    }

    this.apiService
      .query<ExportAudienceMutation, ExportAudienceMutationVariables>(ExportAudience, {
        exportAudienceRequest: {
          queryId,
          expireAt,
          queryName,
          engagement: this.engagement,
          exportedBy: this.userManagementService.getEmail(),
          orgType: this.userManagementService.getOrgType(),
          targetChannel: this.activationChannel.channelType,
          attributesMapping,
          channelId: this.activationChannel?.channelId,
          folderPath,
          fileType,
          audienceFrom: audienceFrom as AudienceType,
        },
      })
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      .then((_response) => {
        this.nzModalService.closeAll();
        this.nzMessageService.success('Request for export audience submitted successfully');
        this.redirectToHistory();
      })
      .catch((error) => {
        this.nzMessageService.error(`Failed to export with error ${error.message}`);
      })
      .finally(() => (this.isLoading = false));
  }

  handleAudienceSelect(): void {
    const { audienceFrom, queryId } = this.exportAudienceForm.value;

    // Return early if no queryId is selected
    if (!queryId) {
      return;
    }

    if (audienceFrom === AudienceType.QUERY) {
      this.handleQuerySelect();
    } else if (audienceFrom === AudienceType.LOOKALIKE_MODEL) {
      this.handleModelSelect();
    }
  }

  private handleQuerySelect(): void {
    const { queryId } = this.exportAudienceForm.value;
    this.queryRecord = this.selectAudienceOptions?.find((queryRecord: QueryRecord) => queryRecord.queryId === queryId);
    if (
      this.activationChannel.channelType !== ChannelType.META &&
      this.activationChannel.channelType !== ChannelType.GOOGLE
    )
      return;
    if (!this.queryRecord && this.queryRecord!.metadata) return;
    const metaData = JSON.parse(this.queryRecord!.metadata!);
    const queryAttributes = metaData.columnsList?.map((column: any) => column.label);
    this.loadAttributes(queryAttributes);
  }

  private handleModelSelect(): void {
    const { queryId } = this.exportAudienceForm.value;
    this.selectedModel = this.selectModelOptions?.find((model: Model) => model.modelId === queryId);

    if (
      this.activationChannel.channelType !== ChannelType.META &&
      this.activationChannel.channelType !== ChannelType.GOOGLE
    )
      return;

    if (!this.selectedModel && this.selectedModel!.metadata) return;
    const metaData = JSON.parse(this.selectedModel!.metadata!);
    const defaultAttributes = metaData.columnsList?.map((column: any) => column.label);
    this.loadAttributes(defaultAttributes);
  }

  disabledDate = (current: Date): boolean => {
    return differenceInCalendarDays(current, this.expiryAtMinDate) < 0;
  };

  get audienceFromControl(): FormControl {
    return this.exportAudienceForm.get('audienceFrom') as FormControl;
  }

  get attributesMappingFormArray(): FormArray<FormGroup> {
    return this.exportAudienceForm.get('attributesMapping') as FormArray<FormGroup>;
  }

  get canExport(): boolean {
    if (this.activationChannel.channelType === ChannelType.META) {
      return !(this.exportAudienceForm.valid && this.atLeastMappingOneEmail);
    } else if (this.activationChannel.channelType === ChannelType.GOOGLE) {
      return !(this.exportAudienceForm.valid && this.hasValidGoogleMappings);
    } else {
      return !this.exportAudienceForm.valid;
    }
  }

  get atLeastMappingOneEmail(): boolean {
    return this.attributesMappingFormArray.value.some((field: any) => field.mapping === 'EMAIL');
  }

  get isMappingEmpty(): boolean {
    return this.attributesMappingFormArray.value.every((field: any) => field.mapping != '');
  }

  get isDuplicateMappings(): boolean {
    const mappingCount = this.attributesMappingFormArray.value.reduce((acc, field) => {
      acc[field.mapping] = (acc[field.mapping] || 0) + 1;
      return acc;
    }, {});
    return Object.values(mappingCount).some((count: any) => count > 1);
  }

  get hasRequiredGoogleMappings(): boolean {
    const mappingValues = this.attributesMappingFormArray.value.map((field: any) => field.mapping);
    return mappingValues.includes(GoogleMappings.EMAIL) && mappingValues.includes(GoogleMappings.PHONE);
  }

  get hasValidGoogleMappings(): boolean {
    const totalColumns = this.attributesMappingFormArray.controls.length;
    const mappingValues = this.attributesMappingFormArray.value.map((field: any) => field.mapping);

    if (totalColumns === 1) {
      // If only one column, allow either Email or Phone mapping
      return mappingValues.includes(GoogleMappings.EMAIL) || mappingValues.includes(GoogleMappings.PHONE);
    } else if (totalColumns == 2) {
      // If two or more columns, require both Email and Phone mappings
      return mappingValues.includes(GoogleMappings.EMAIL) && mappingValues.includes(GoogleMappings.PHONE);
    } else if (totalColumns > 2) {
      // If more than two columns then invalid query.
      return false;
    }

    return false;
  }

  getGuidelines(attributeMapping: string) {
    return this.mappingOptions.find((mapping: any) => attributeMapping === mapping.value)?.guidelines;
  }

  onAudienceFromChange(audienceFrom: AudienceType): void {
    this.exportAudienceForm.patchValue({ queryId: '' });
    this.queryRecord = undefined;
    this.selectedModel = undefined;

    if (this.attributesMappingFormArray) {
      this.attributesMappingFormArray.clear();
    }

    if (audienceFrom === AudienceType.LOOKALIKE_MODEL) {
      this.getSelectModelOptions();
    } else {
      this.selectModelOptions = undefined;
    }
  }

  redirectToHistory() {
    this.router.navigate(['/query'], {
      queryParams: { tabIndex: 3 },
    });
  }
}
